import Http from '@services/httpClient';
import { ClientRequest } from '@common/ApiTypes';
import AuthStorage from '@common/services/authStorage';

const prefix = '/api/v1/llm/cv';
const BASE_URL = process.env.REACT_APP_BASE_URL || '';

function stripSsePrefix(line: string): string {
  const t = line.trim();
  return t.startsWith('data: ') ? t.slice(6).trim() : t;
}

// Full JSON parse first; falls back to regex-extracting complete strings
// from a partial {"recommendations": [...]} buffer for progressive display
function tryExtractFlat(assembled: string): string[] {
  try {
    const parsed = JSON.parse(assembled);
    if (Array.isArray(parsed?.recommendations)) return parsed.recommendations;
    if (Array.isArray(parsed)) return parsed;
    if (typeof parsed === 'string' && parsed) return [parsed];
    return [];
  } catch {
    const match = assembled.match(/"recommendations"\s*:\s*\[([\s\S]*)/);
    if (!match) return [];
    const items: string[] = [];
    const re = /"((?:[^"\\]|\\.)*)"/g;
    let m = re.exec(match[1]);
    while (m) {
      items.push(m[1]);
      m = re.exec(match[1]);
    }
    return items;
  }
}

// Grouped structure can't be partially extracted cleanly — full JSON parse only
function tryExtractGrouped(assembled: string): Record<string, string[]> {
  try {
    const parsed = JSON.parse(assembled);
    if (
      parsed?.recommendations &&
      typeof parsed.recommendations === 'object' &&
      !Array.isArray(parsed.recommendations)
    ) {
      return parsed.recommendations as Record<string, string[]>;
    }
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, string[]>;
    }
  } catch {
    // Incomplete JSON — keep buffering
  }
  return {};
}

async function openStream(
  url: string,
  data: object,
): Promise<ReadableStreamDefaultReader<Uint8Array>> {
  const token = AuthStorage.getInstance().getAccessToken();
  const response = await fetch(`${BASE_URL}${url}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { Authorization: token } : {}),
    },
    body: JSON.stringify(data),
    credentials: 'include',
  });

  if (response.status === 401) {
    AuthStorage.getInstance().clearAll();
    window.location.reload();
    throw new Error('Unauthorized');
  }
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(
      (err as any).message || `Request failed (${response.status})`,
    );
  }
  if (!response.body) throw new Error('No readable stream in response');
  return response.body.getReader();
}

async function streamPost(
  url: string,
  data: object,
  onItems: (accumulated: string[]) => void,
): Promise<void> {
  const reader = await openStream(url, data);
  const decoder = new TextDecoder();
  let lineBuffer = ''; // buffers incomplete SSE lines within a TCP packet
  // assembles {"chunk":"..."} pieces into the full payload
  let chunkBuffer = '';
  const accumulated: string[] = [];

  function processLine(line: string): void {
    const text = stripSsePrefix(line);
    if (!text || text === '[DONE]') return;

    // Each line arrives as {"chunk": "<piece>"} — accumulate the pieces
    try {
      const envelope = JSON.parse(text);
      if (typeof envelope.chunk === 'string') {
        chunkBuffer += envelope.chunk;
      }
    } catch {
      // Not an envelope — treat raw text as the chunk piece itself
      chunkBuffer += text;
    }

    let changed = false;
    tryExtractFlat(chunkBuffer).forEach((item) => {
      if (!accumulated.includes(item)) {
        accumulated.push(item);
        changed = true;
      }
    });
    if (changed) onItems([...accumulated]);
  }

  try {
    let streamDone = false;
    while (!streamDone) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await reader.read();
      streamDone = done;
      if (!done) {
        lineBuffer += decoder.decode(value, { stream: true });
        const lines = lineBuffer.split('\n');
        lineBuffer = lines.pop() ?? '';
        lines.forEach(processLine);
      }
    }
    if (lineBuffer.trim()) processLine(lineBuffer);
  } finally {
    reader.releaseLock();
  }
}

async function streamPostGrouped(
  url: string,
  data: object,
  onItems: (accumulated: Record<string, string[]>) => void,
): Promise<void> {
  const reader = await openStream(url, data);
  const decoder = new TextDecoder();
  let lineBuffer = '';
  let chunkBuffer = '';
  const accumulated: Record<string, string[]> = {};

  function processLine(line: string): void {
    const text = stripSsePrefix(line);
    if (!text || text === '[DONE]') return;

    try {
      const envelope = JSON.parse(text);
      if (typeof envelope.chunk === 'string') {
        chunkBuffer += envelope.chunk;
      }
    } catch {
      chunkBuffer += text;
    }

    let changed = false;
    Object.entries(tryExtractGrouped(chunkBuffer)).forEach(([group, items]) => {
      if (!accumulated[group]) accumulated[group] = [];
      (items as string[]).forEach((item) => {
        if (!accumulated[group].includes(item)) {
          accumulated[group].push(item);
          changed = true;
        }
      });
    });
    if (changed) onItems({ ...accumulated });
  }

  try {
    let streamDone = false;
    while (!streamDone) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await reader.read();
      streamDone = done;
      if (!done) {
        lineBuffer += decoder.decode(value, { stream: true });
        const lines = lineBuffer.split('\n');
        lineBuffer = lines.pop() ?? '';
        lines.forEach(processLine);
      }
    }
    if (lineBuffer.trim()) processLine(lineBuffer);
  } finally {
    reader.releaseLock();
  }
}

export default {
  parseCV(data: FormData) {
    return new Promise((resolve, reject) => {
      const req: ClientRequest = {
        data,
        url: `${prefix}/parse`,
      };

      Http.postUpload(req)
        .then((res) => resolve(res))
        .catch((e) => reject(e));
    });
  },

  recommWorkProfile(data: object) {
    return new Promise((resolve, reject) => {
      const req: ClientRequest = {
        data,
        url: `${prefix}/generate/work-profile`,
      };

      Http.post(req)
        .then((res) => resolve(res))
        .catch((e) => reject(e));
    });
  },

  recommWorkExp(data: object) {
    return new Promise((resolve, reject) => {
      const req: ClientRequest = {
        data,
        url: `${prefix}/generate/work-experience`,
      };

      Http.post(req)
        .then((res) => resolve(res))
        .catch((e) => reject(e));
    });
  },

  recommEducations(data: object) {
    return new Promise((resolve, reject) => {
      const req: ClientRequest = {
        data,
        url: `${prefix}/generate/educations`,
      };

      Http.post(req)
        .then((res) => resolve(res))
        .catch((e) => reject(e));
    });
  },

  recommSkills(data: object) {
    return new Promise((resolve, reject) => {
      const req: ClientRequest = {
        data,
        url: `${prefix}/generate/skills`,
      };

      Http.post(req)
        .then((res) => resolve(res))
        .catch((e) => reject(e));
    });
  },

  streamRecommWorkProfile(data: object, onItems: (items: string[]) => void) {
    return streamPost(`${prefix}/generate/work-profile/stream`, data, onItems);
  },
  streamRecommWorkExp(data: object, onItems: (items: string[]) => void) {
    return streamPost(
      `${prefix}/generate/work-experience/stream`,
      data,
      onItems,
    );
  },
  streamRecommEducations(data: object, onItems: (items: string[]) => void) {
    return streamPost(`${prefix}/generate/educations/stream`, data, onItems);
  },
  streamRecommSkills(
    data: object,
    onItems: (groups: Record<string, string[]>) => void,
  ) {
    return streamPostGrouped(`${prefix}/generate/skills/stream`, data, onItems);
  },
};
